import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Filter, Users, Linkedin, Mail, Phone, Building,
  MoreVertical, Edit, Trash2, X, Calendar, DollarSign, Tag,
  MessageSquare, ArrowRight, TrendingUp, Target, Clock, Upload, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const statusOptions = [
  { value: 'new', label: 'Nouveau', color: 'bg-slate-100 text-slate-600' },
  { value: 'contacted', label: 'Contacté', color: 'bg-blue-100 text-blue-600' },
  { value: 'qualified', label: 'Qualifié', color: 'bg-purple-100 text-purple-600' },
  { value: 'proposal', label: 'Proposition', color: 'bg-amber-100 text-amber-600' },
  { value: 'negotiation', label: 'Négociation', color: 'bg-orange-100 text-orange-600' },
  { value: 'won', label: 'Gagné', color: 'bg-green-100 text-green-600' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-600' },
];

const sourceOptions = [
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'website', label: 'Site web', icon: Target },
  { value: 'referral', label: 'Recommandation', icon: Users },
  { value: 'cold_call', label: 'Appel sortant', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'event', label: 'Événement', icon: Calendar },
  { value: 'other', label: 'Autre', icon: Target },
];

const activityTypes = [
  { value: 'call', label: 'Appel', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'meeting', label: 'Rendez-vous', icon: Calendar },
  { value: 'note', label: 'Note', icon: MessageSquare },
];

export default function CRMPage() {
  const { user } = useOutletContext();
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list or kanban
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [newProspect, setNewProspect] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    linkedin_url: '',
    source: 'linkedin',
    notes: '',
    tags: [],
    estimated_value: 0
  });
  const [newActivity, setNewActivity] = useState({ type: 'note', description: '' });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [filterStatus, filterSource]);

  const fetchProspects = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      let url = `${API_URL}/api/crm/prospects`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterSource) params.append('source', filterSource);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProspects(data);
      }
    } catch (error) {
      console.error('Error fetching prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateProspect = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/crm/prospects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProspect)
      });

      if (response.ok) {
        toast.success('Prospect créé avec succès');
        setShowCreateModal(false);
        setNewProspect({
          name: '', company: '', email: '', phone: '', linkedin_url: '',
          source: 'linkedin', notes: '', tags: [], estimated_value: 0
        });
        fetchProspects();
        fetchStats();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (prospectId, newStatus) => {
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/crm/prospects/${prospectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchProspects();
        fetchStats();
        if (selectedProspect?.id === prospectId) {
          setSelectedProspect({ ...selectedProspect, status: newStatus });
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAddActivity = async (prospectId) => {
    if (!newActivity.description.trim()) return;
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(
        `${API_URL}/api/crm/prospects/${prospectId}/activities?activity_type=${newActivity.type}&description=${encodeURIComponent(newActivity.description)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Activité ajoutée');
        setNewActivity({ type: 'note', description: '' });
        // Refresh prospect details
        const updatedProspect = await fetchProspectDetails(prospectId);
        setSelectedProspect(updatedProspect);
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'activité");
    }
  };

  const fetchProspectDetails = async (prospectId) => {
    const token = localStorage.getItem('client_token');
    const response = await fetch(`${API_URL}/api/crm/prospects/${prospectId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) return await response.json();
    return null;
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!confirm('Es-tu sûr de vouloir supprimer ce prospect ?')) return;
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/crm/prospects/${prospectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Prospect supprimé');
        fetchProspects();
        fetchStats();
        setShowDetailModal(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openProspectDetail = async (prospect) => {
    const details = await fetchProspectDetails(prospect.id);
    setSelectedProspect(details || prospect);
    setShowDetailModal(true);
  };

  const filteredProspects = prospects.filter(prospect =>
    prospect.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    prospect.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    prospect.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
  const getSourceInfo = (source) => sourceOptions.find(s => s.value === source) || sourceOptions[6];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  // Kanban view grouping
  const prospectsByStatus = statusOptions.reduce((acc, status) => {
    acc[status.value] = filteredProspects.filter(p => p.status === status.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Mon Cockpit - Prospection</h1>
          <p className="text-rm-text-secondary">Gérez tes prospects LinkedIn et autres sources</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            className="border-rm-primary text-rm-primary hover:bg-rm-primary/5"
            data-testid="import-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer CSV
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-rm-primary hover:bg-rm-primary/90"
            data-testid="new-prospect-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouveau prospect
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.total_prospects || 0}</p>
              <p className="text-xs text-rm-text-secondary">Total prospects</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.by_status?.qualified || 0}</p>
              <p className="text-xs text-rm-text-secondary">Qualifiés</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{stats.by_status?.won || 0}</p>
              <p className="text-xs text-rm-text-secondary">Gagnés</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rm-text">{(stats.total_estimated_value || 0).toLocaleString()}€</p>
              <p className="text-xs text-rm-text-secondary">Valeur estimée</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un prospect..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 text-sm"
          >
            <option value="">Tous les statuts</option>
            {statusOptions.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 text-sm"
          >
            <option value="">Toutes les sources</option>
            {sourceOptions.map(source => (
              <option key={source.value} value={source.value}>{source.label}</option>
            ))}
          </select>
          <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-rm-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              Liste
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-2 text-sm ${viewMode === 'kanban' ? 'bg-rm-primary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              Pipeline
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          {filteredProspects.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-rm-text-secondary">Aucun prospect trouvé</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-rm-primary">
                <Plus className="w-4 h-4 mr-2" /> Ajouter un prospect
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Prospect</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Entreprise</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Source</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Valeur</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-rm-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProspects.map((prospect) => {
                    const statusInfo = getStatusInfo(prospect.status);
                    const sourceInfo = getSourceInfo(prospect.source);
                    const SourceIcon = sourceInfo.icon;

                    return (
                      <tr 
                        key={prospect.id} 
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => openProspectDetail(prospect)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-rm-primary font-semibold">
                                {prospect.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-rm-text">{prospect.name}</p>
                              <p className="text-xs text-rm-text-secondary">{prospect.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-rm-text">{prospect.company || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs text-rm-text-secondary">
                            <SourceIcon className="w-3 h-3" />
                            {sourceInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={prospect.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleUpdateStatus(prospect.id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-1 rounded-full border-0 ${statusInfo.color}`}
                          >
                            {statusOptions.map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-rm-text">
                            {prospect.estimated_value ? `${prospect.estimated_value.toLocaleString()}€` : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {prospect.linkedin_url && (
                              <a
                                href={prospect.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-1 hover:bg-blue-100 rounded text-blue-500"
                              >
                                <Linkedin className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProspect(prospect.id);
                              }}
                              className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {statusOptions.slice(0, 6).map((status) => (
            <div key={status.value} className="flex-shrink-0 w-72">
              <div className={`rounded-t-xl px-3 py-2 ${status.color}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{status.label}</span>
                  <span className="text-xs bg-white/50 px-2 py-0.5 rounded-full">
                    {prospectsByStatus[status.value]?.length || 0}
                  </span>
                </div>
              </div>
              <div className="bg-slate-100 rounded-b-xl p-2 min-h-[400px] space-y-2">
                {prospectsByStatus[status.value]?.map((prospect) => (
                  <motion.div
                    key={prospect.id}
                    layout
                    className="bg-white rounded-lg p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openProspectDetail(prospect)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-rm-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-rm-primary text-sm font-semibold">
                          {prospect.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-rm-text truncate">{prospect.name}</p>
                        <p className="text-xs text-rm-text-secondary truncate">{prospect.company}</p>
                      </div>
                    </div>
                    {prospect.estimated_value > 0 && (
                      <p className="text-xs font-medium text-green-600">
                        {prospect.estimated_value.toLocaleString()}€
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-rm-text">Nouveau prospect</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateProspect} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-rm-text mb-1">Nom *</label>
                    <input
                      type="text"
                      required
                      value={newProspect.name}
                      onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-rm-text mb-1">Entreprise</label>
                    <input
                      type="text"
                      value={newProspect.company}
                      onChange={(e) => setNewProspect({ ...newProspect, company: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="Entreprise SAS"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Email</label>
                    <input
                      type="email"
                      value={newProspect.email}
                      onChange={(e) => setNewProspect({ ...newProspect, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="jean@entreprise.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={newProspect.phone}
                      onChange={(e) => setNewProspect({ ...newProspect, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="06 00 00 00 00"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-rm-text mb-1">Profil LinkedIn</label>
                    <input
                      type="url"
                      value={newProspect.linkedin_url}
                      onChange={(e) => setNewProspect({ ...newProspect, linkedin_url: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="https://linkedin.com/in/jeandupont"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Source</label>
                    <select
                      value={newProspect.source}
                      onChange={(e) => setNewProspect({ ...newProspect, source: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {sourceOptions.map(source => (
                        <option key={source.value} value={source.value}>{source.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Valeur estimée (€)</label>
                    <input
                      type="number"
                      min="0"
                      value={newProspect.estimated_value}
                      onChange={(e) => setNewProspect({ ...newProspect, estimated_value: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="1000"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-rm-text mb-1">Notes</label>
                    <textarea
                      value={newProspect.notes}
                      onChange={(e) => setNewProspect({ ...newProspect, notes: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      rows={3}
                      placeholder="Notes sur le prospect..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-rm-primary">
                    Créer le prospect
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedProspect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-rm-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-rm-primary text-xl font-bold">
                        {selectedProspect.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-rm-text">{selectedProspect.name}</h2>
                      <p className="text-rm-text-secondary">{selectedProspect.company}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-3 mt-4">
                  <select
                    value={selectedProspect.status}
                    onChange={(e) => handleUpdateStatus(selectedProspect.id, e.target.value)}
                    className={`text-sm px-3 py-1.5 rounded-full border-0 font-medium ${getStatusInfo(selectedProspect.status).color}`}
                  >
                    {statusOptions.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {selectedProspect.estimated_value > 0 && (
                    <span className="text-sm px-3 py-1.5 bg-green-100 text-green-600 rounded-full">
                      {selectedProspect.estimated_value.toLocaleString()}€
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {selectedProspect.email && (
                    <a href={`mailto:${selectedProspect.email}`} className="flex items-center gap-2 text-sm text-rm-text-secondary hover:text-rm-primary">
                      <Mail className="w-4 h-4" />
                      {selectedProspect.email}
                    </a>
                  )}
                  {selectedProspect.phone && (
                    <a href={`tel:${selectedProspect.phone}`} className="flex items-center gap-2 text-sm text-rm-text-secondary hover:text-rm-primary">
                      <Phone className="w-4 h-4" />
                      {selectedProspect.phone}
                    </a>
                  )}
                  {selectedProspect.linkedin_url && (
                    <a href={selectedProspect.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600">
                      <Linkedin className="w-4 h-4" />
                      Voir le profil LinkedIn
                    </a>
                  )}
                </div>

                {/* Notes */}
                {selectedProspect.notes && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-rm-text mb-2">Notes</h3>
                    <p className="text-sm text-rm-text-secondary bg-slate-50 rounded-lg p-3">
                      {selectedProspect.notes}
                    </p>
                  </div>
                )}

                {/* Add Activity */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-rm-text mb-2">Ajouter une activité</h3>
                  <div className="flex gap-2">
                    <select
                      value={newActivity.type}
                      onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      {activityTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newActivity.description}
                      onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                      placeholder="Description de l'activité..."
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    <Button
                      onClick={() => handleAddActivity(selectedProspect.id)}
                      size="sm"
                      className="bg-rm-primary"
                    >
                      Ajouter
                    </Button>
                  </div>
                </div>

                {/* Activity History */}
                <div>
                  <h3 className="text-sm font-medium text-rm-text mb-2">Historique</h3>
                  <div className="space-y-3">
                    {selectedProspect.activities?.length > 0 ? (
                      selectedProspect.activities.slice().reverse().map((activity, index) => {
                        const activityType = activityTypes.find(t => t.value === activity.type);
                        const ActivityIcon = activityType?.icon || MessageSquare;
                        return (
                          <div key={index} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                              <ActivityIcon className="w-4 h-4 text-rm-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-rm-text">{activity.description}</p>
                              <p className="text-xs text-rm-text-secondary mt-1">
                                {activity.user_name} • {formatDate(activity.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-rm-text-secondary text-center py-4">
                        Aucune activité enregistrée
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-100 flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => handleDeleteProspect(selectedProspect.id)}
                  className="text-red-500 hover:text-red-600 hover:border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <Button onClick={() => setShowDetailModal(false)}>
                  Fermer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import CSV Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !importLoading && setShowImportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-rm-primary/10 rounded-lg flex items-center justify-center">
                      <Linkedin className="w-5 h-5 text-rm-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-rm-text">Importer des prospects</h2>
                      <p className="text-sm text-rm-text-secondary">Depuis un export LinkedIn CSV</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => !importLoading && setShowImportModal(false)} 
                    className="p-2 hover:bg-slate-100 rounded-lg"
                    disabled={importLoading}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <h3 className="font-medium text-rm-text mb-2">Comment exporter depuis LinkedIn ?</h3>
                  <ol className="text-sm text-rm-text-secondary space-y-2 list-decimal list-inside">
                    <li>Allez dans <strong>LinkedIn &gt; Préférences &gt; Obtenir une copie de tes données</strong></li>
                    <li>Sélectionnez <strong>"Connections"</strong> et demandez l'export</li>
                    <li>Télécharge le fichier CSV reçu par email</li>
                    <li>Importez-le ici !</li>
                  </ol>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    setImportLoading(true);
                    const token = localStorage.getItem('client_token');
                    
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      
                      const response = await fetch(`${API_URL}/api/crm/prospects/import-csv`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formData
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        toast.success(`${result.imported} prospects importés ! (${result.skipped} ignorés)`);
                        setShowImportModal(false);
                        fetchProspects();
                        fetchStats();
                      } else {
                        const error = await response.json();
                        toast.error(error.detail || "Erreur lors de l'import");
                      }
                    } catch (error) {
                      toast.error("Erreur lors de l'import du fichier");
                    } finally {
                      setImportLoading(false);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  }}
                />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importLoading}
                  className="w-full border-2 border-dashed border-slate-200 rounded-xl p-8 hover:border-rm-primary hover:bg-rm-primary/5 transition-colors disabled:opacity-50"
                  data-testid="upload-csv-dropzone"
                >
                  {importLoading ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-rm-primary mb-3" />
                      <p className="text-rm-text font-medium">Import en cours...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <FileText className="w-12 h-12 text-slate-400 mb-3" />
                      <p className="text-rm-text font-medium">Cliquez pour sélectionner un fichier CSV</p>
                      <p className="text-sm text-rm-text-secondary mt-1">ou glisse-dépose ton fichier ici</p>
                    </div>
                  )}
                </button>

                <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Note :</strong> Les prospects existants (même email ou URL LinkedIn) seront ignorés pour éviter les doublons.
                  </p>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 flex justify-end">
                <Button variant="outline" onClick={() => setShowImportModal(false)} disabled={importLoading}>
                  Fermer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
